import React, { useState, useEffect } from 'react';
import { Link } from 'gatsby';
import styled from 'styled-components';
import { AccessTime } from '@material-ui/icons';
import _ from 'lodash';

import { rhythm } from '../utils/typography';
import { getReadDuration } from '../utils/read';
import { from, Breakpoints } from '../utils/theme';

const Article = styled.article`
  padding: ${rhythm(1)};
  position: relative;
  border: 1px solid rgba(255, 255, 255, 0.12);
  border-radius: 5px;
  box-shadow: 0 30px 60px -10px rgba(0, 0, 0, 0.3),
    0 18px 36px -18px rgba(0, 0, 0, 0.33);
  color: var(--text-normal);
  background-color: var(--post-bg);
  display: block;
  margin-bottom: ${rhythm(2)};
  transition: transform 0.3s ease-in-out, box-shadow 0.3s ease-in-out;

  ${from(Breakpoints.MD, `padding: ${rhythm(2)};`)}

  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 50px 80px -20px rgba(0, 0, 0, 0.27),
      0 30px 50px -30px rgba(0, 0, 0, 0.3);
  }
`;

const Header = styled.header`
  text-align: left;
  margin-bottom: ${rhythm(1)};
`;

const Title = styled.h3`
  color: var(--text-normal);
  margin-top: 0;
  margin-bottom: ${rhythm(0.2)};
`;

const Description = styled.p`
  text-align: left;
  margin-bottom: 0;
`;

const Eyebrow = styled.small`
  display: flex;
  color: var(--text-eyebrow);
`;

const Clock = styled(AccessTime)`
  font-size: ${rhythm(0.5)};
  margin: 0 ${rhythm(0.1)};
`;

const TagList = styled.ul`
  align-items: center;
  list-style: none;
  margin: 0;
  margin-top: ${rhythm(0.2)};
  padding: 0;
  display: none;
  flex-direction: row;

  ${from(Breakpoints.MD, 'display: flex;')}
`;

const Tag = styled.li`
  margin-bottom: 0;
  margin-left: ${rhythm(0.4)};
  margin-right: ${rhythm(0.4)};
  font-size: ${rhythm(0.4)};

  &:before {
    content: '\\2022';
    color: var(--text-normal);
    font-size: ${rhythm(0.5)};
    font-weight: bold;
    display: inline-block;
    width: 15px;
    margin-left: -15px;
    box-sizing: border-box;
    text-align: left;
  }

  &:first-child {
    margin-left: 0;

    &:before {
      content: '';
      width: 0;
      margin-left: 0;
    }
  }

  &:last-child {
    margin-right: 0;
  }
`;

const Nav = styled(Link)`
  box-shadow: none;
  display: block;

  &:hover {
    text-decoration: none;
  }
`;

const TagLink = styled(Link)`
  box-shadow: none;

  &:hover {
    box-shadow: 0 1px 0 0 currentColor;
  }
`;

const ReadDuration = styled.span`
  display: flex;
  align-items: center;
`;

export type Node = {
  id: string;
  excerpt: string;
  fields: {
    slug: string;
  };
  frontmatter: {
    title: string;
    date: string;
    description: string;
    tags: string[];
  };
  html: string;
  htmlAst: string;
};

type Props = {
  node: Node;
};

const Post: React.FC<Props> = ({ node }) => {
  const [duration, setDuration] = useState('');
  const title = node.frontmatter.title || node.fields.slug;
  const { tags } = node.frontmatter;

  useEffect(() => {
    setDuration(getReadDuration(document, node.html));
  }, []);

  return (
    <Article>
      <Header>
        <Nav to={node.fields.slug}>
          <Title>{title}</Title>
          <Eyebrow>
            {node.frontmatter.date}
            <ReadDuration>
              {', '}
              <Clock />
              {duration}
            </ReadDuration>
          </Eyebrow>
        </Nav>
        {tags && (
          <>
            <TagList>
              {tags.map((tag, i) => (
                <Tag key={i}>
                  <TagLink to={`/tags/${_.kebabCase(tag)}`}>{tag}</TagLink>
                </Tag>
              ))}
            </TagList>
          </>
        )}
      </Header>
      <section>
        <Description
          // eslint-disable-next-line react/no-danger
          dangerouslySetInnerHTML={{
            __html: node.frontmatter.description || node.excerpt,
          }}
        />
      </section>
    </Article>
  );
};

export default Post;
